<template>
  <div class="two-locations contacts-terminals terminals-geo-panel">
    <div class="mb-20 flex flex-align-items-center">
      <div class="red-dot" />{{ message }} {{ rclLocation?.name }}
    </div>
    <contacts-terminals-slider
      v-model="selectedTerminalValue"
      :location-guid="rclLocation.guid"
      :purpose="purpose"
      @change="onTerminalSelect"
    />
    <div class="flex two-locations-map terminals-geo">
      <vz-yandex-map
        v-model="map"
        v-model:location="locationValue"
        :markers="locationTerminals"
        :has-search="false"
        @marker-click="onTerminalSelect"
      >
        <template #map-additional>
          <yandex-map-controls :settings="{ position: 'left' }">
            <yandex-map-zoom-control />
          </yandex-map-controls>
        </template>
      </vz-yandex-map>

      <contacts-terminals-card
        has-address
        :terminal-guid="selectedTerminal?.guid"
      />
    </div>

    <div class="two-locations-print">
      <nuxt-link :to="`/mapScheme?g=${selectedTerminal?.guid}`" target="_blank" class="text-decoration-underline">
        Распечатать контакты
      </nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import {storeToRefs} from 'pinia'
import {Location, Terminal} from '~~/types/types'
import {useTerminalStore} from '~/stores/terminal'
import {useCityStore} from '~/stores/city'
import VzYandexMap from '~~/components/ui-kit/map/vz-yandex-map.vue'
import ContactsTerminalsSlider from '~~/components/public/contacts/contacts-terminals-slider.vue'
import ContactsTerminalsCard from '~~/components/public/contacts/contacts-terminals-card.vue'
import {getAddressByGuid} from "~/api/city";
import {asyncComputed} from "@vueuse/core";
import type {YMap} from '@yandex/ymaps3-types';
import {YandexMapControls, YandexMapZoomControl} from 'vue-yandex-maps';
import {PropType} from 'vue';

const props = defineProps({
  isFrom: {
    type: Boolean,
    default: false
  }
})

const {terminalList} = storeToRefs(useTerminalStore())
const {terminalSelectByLocation} = useTerminalStore()
const {from, to} = storeToRefs(useCityStore())

const map = shallowRef<null | YMap>(null)
const selectedTerminal = ref<Terminal | null>()
const selectedTerminalValue = ref('')

const rclLocation = asyncComputed(async () => {
  const cl = props.isFrom ? from : to
  if (cl.value.rcl_guid) {
    const {location} = await getAddressByGuid(cl.value.rcl_guid)
    return location;
  }

  return cl.value;
}, {} as Location)

const message = computed(() => props.isFrom ? 'Отправление' : 'Прибытие')

const purpose = computed(() => props.isFrom ? 'from' : 'to' )

const locationValue = computed(() => ({
  center: selectedTerminal.value?.coordinates || [0, 0],
  zoom: 10,
  duration: 500,
}))

const locationTerminals = computed(() => {
  const data = terminalSelectByLocation(rclLocation.value.guid, purpose.value)
  return data.filter(
    terminal => terminal.guid !== 'is_custom'
  ).map(t => ({
    ...t,
    coordinates: [...t.coordinates].reverse(),
    showTooltip: t.guid === selectedTerminal.value?.guid,
    icon: {
      name: 'map-marker',
      size: 16
    },
  }))
})

watch(
  () => locationTerminals.value.toString(),
  () => {
    selectCurrentTerminal()
  },
  {immediate: true}
)

function selectCurrentTerminal(): void {
  const terminalAddress = useRoute().query.terminal

  if (!locationTerminals.value.length) {
    selectedTerminal.value = null
    return
  }
  const findTerminal = terminalAddress ? locationTerminals.value.find(t => t.address === terminalAddress) : locationTerminals.value[0]
  if (findTerminal) {
    onTerminalSelect(findTerminal)
  }
}

function onTerminalSelect({guid}: Terminal) {
  selectedTerminal.value = null
  if (guid !== 'is_custom') {
    selectedTerminal.value = locationTerminals.value.find(t => t.guid === guid)
    selectedTerminalValue.value = guid
  }
}

watch(() => terminalList.value.length, () => {
  if (!selectedTerminal.value) {
    selectedTerminal.value = terminalList.value.find(t => t.location?.guid === (props.isFrom ? from.value?.guid : to.value?.guid))
  }
}, {immediate: true})
</script>

<style lang="scss" scoped>
.two-locations {
  margin-top: 30px;

  &-print {
    margin-top: 20px;
    display: flex;
    justify-content: right;
  }

  :deep(.contacts-terminals-card) {
    margin-bottom: 0;
  }

  :deep(.contacts-terminals-card) {
    position: relative;
    box-shadow: unset;
    margin-top: 0;
    width: 610px;
    height: 350px;

    .contacts-terminals-card-body {
      margin-top: 0;
      padding-top: 0;
      padding-bottom: 0;
      height: 350px;
    }

    .contacts-terminals-card-subtitle {
      margin-top: 28px;
      &--margin-none {
        margin-top: 0;
      }
    }
  }

  @media only screen and (max-width: $pub-md) {
    :deep(.vz-yandex-map) {
      width: 513px !important;
    }

    :deep(.contacts-terminals-card) {
      width: 513px;
    }
  }

  @media only screen and (max-width: $pub-sm) {
    :deep(.vz-yandex-map) {
      width: 358px !important;
    }


    :deep(.contacts-terminals-card) {
      height: 400px;
      width: 380px;

      .contacts-terminals-card-body {
        height: 400px;
      }
    }
  }

  @media only screen and (max-width: $pub-xs) {
    .two-locations-map {
      flex-direction: column;
      gap: 20px;
      align-items: center;
      width: 100%;
    }

    :deep(.vz-yandex-map) {
      width: 100% !important;
    }

    :deep(.contacts-terminals-card) {
      height: 400px;
      width: 100%;
      margin-top: 20px;


      .contacts-terminals-card-body {
        height: 400px;
      }
    }
  }
}
</style>
